/* eslint-disable prettier/prettier */
import { useEffect, useState, useRef } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { createPortal } from "react-dom";
import { useMaterialUIController, setUser } from "context";
import ChangePassword from "layouts/dashboards/user/changePassword";
import updateCountry from "api/users/updateCountry";
import AddCredential from "layouts/dashboards/user/addCredential";
import ContractOverview from "layouts/dashboards/user/contractOverview";
import GeneralInformation from "layouts/dashboards/user/generalInformation";
import PersonalCredentialsBox from "layouts/dashboards/user/credentials/personalCredentialsBox";
import OtherCredentials from "layouts/dashboards/user/credentials/otherCredentials";
import Autocomplete from "layouts/dashboards/stealers/autocomplete";

// Material Dashboard 2 PRO React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

import SecurityIcon from "@mui/icons-material/Security";
import VisibilityIcon from "@mui/icons-material/Visibility";
import PersonIcon from "@mui/icons-material/Person";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import EmailIcon from "@mui/icons-material/Email";
import GroupIcon from "@mui/icons-material/Group";
import LockIcon from "@mui/icons-material/Lock";
import NotificationsIcon from "@mui/icons-material/Notifications";
import AssessmentIcon from "@mui/icons-material/Assessment";
import CampaignIcon from "@mui/icons-material/Campaign";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
// Material Dashboard 2 PRO React components
import MDBox from "components/MDBox";
import SweetAlert from "react-bootstrap-sweetalert";

// @mui material Icons
import IconButton from "@mui/material/IconButton";
import ServerError from "utilities/ServerError";
import setNotificationsSettings from "api/users/setNotificationsSettings";
import countryCode from "config/countryCode";

function UserPage() {
  // eslint-disable-next-line
  const [controller, dispatch] = useMaterialUIController();
  const { user, customers } = controller;
  const [changingPassword, setChangingPassword] = useState(false);
  const [changePasswordSuccess, setChangePasswordSuccess] = useState(false);
  const [changePasswordError, setChangePasswordError] = useState(false);
  const [addingCredential, setAddingCredential] = useState(false);
  const [clientForCredential, setClientForCredential] = useState("");
  const [editUserCountry, setEditUserCountry] = useState(false);
  const [addingCredentialsResult, setAddingCredentialsResult] = useState("");
  const [submitState, setSubmitState] = useState({
    success: false,
    loading: false,
    error: "",
  });
  const [form, setForm] = useState({
    reports: {
      weekly: false,
      monthly: false,
      quarterly: false,
    },
    alerts: {
      email: false,
      push: false,
    },
    marketing: {
      email: false,
      push: false,
    },
  });
  const { t } = useTranslation();

  useEffect(() => {
    if (user) {
      if (user.mustChangePassword) setChangingPassword(true);
      if (user.notificationsSettings) setForm(user.notificationsSettings);
    }
  }, [user]);

  const handleChange = (event) => {
    const { name, checked } = event.target;
    const [category, type] = name.split(".");

    setForm((prevValues) => ({
      ...prevValues,
      [category]: {
        ...prevValues[category],
        [type]: checked,
      },
    }));
  };

  const changeUserCountry = (newCountry) => {
    updateCountry(newCountry, user.id)
      .then((res) => {
        setEditUserCountry(false);
        if (res && res.id) {
          const newUser = Object.assign(user, { country: res.country });
          setUser(dispatch, newUser);
        }
      })
      .catch((err) => {
        console.log("error while updating user country", err);
      });
  };

  const handleFormSubmit = async (event) => {
    event.preventDefault();
    setSubmitState({ ...submitState, loading: true, success: false });
    try {
      await setNotificationsSettings(form);
      setSubmitState({ ...submitState, success: true, loading: false });
      user.notificationsSettings = form;
    } catch (error) {
      setSubmitState({ success: false, loading: false, error: t("user.error") });
      console.log("error --->", error);
    }
  };

  function haveAndCanAddCredentials(customer, type, limitName) {
    const haveCredentials =
      !customer.limits ||
      customer.limits.length === 0 ||
      (customer.limits &&
        customer.limits.some((el) => el && el.type && el.type === limitName && el.limit > 0));
    const canAddCredentials =
      haveCredentials &&
      (!customer.limits ||
        customer.limits.length === 0 ||
        !customer[type] ||
        customer[type].length <
          customer.limits[
            customer.limits.findIndex(
              (el) => el && el.type && el.type === limitName && el.limit > 0
            )
          ].limit);
    return {
      haveCredentials: haveCredentials || false,
      canAddCredentials: canAddCredentials || false,
    };
  }

  function getAvailabilityOfCredentialsOptions(customer) {
    const options = [];
    const combosAvailability = haveAndCanAddCredentials(customer, "combolists", "combos");
    const haveCombos = combosAvailability.haveCredentials;
    const canAddCombos = combosAvailability.canAddCredentials;
    const birthDatesAvailability = haveAndCanAddCredentials(customer, "birthDates", "birthDates");
    const haveBirthDates = birthDatesAvailability.haveCredentials;
    const canAddBirthDates = birthDatesAvailability.canAddCredentials;
    const phonesAvailability = haveAndCanAddCredentials(customer, "phones", "phones");
    const havePhones = phonesAvailability.haveCredentials;
    const canAddPhones = phonesAvailability.canAddCredentials;
    const addressesAvailability = haveAndCanAddCredentials(customer, "addresses", "addresses");
    const haveAddresses = addressesAvailability.haveCredentials;
    const canAddAddresses = addressesAvailability.canAddCredentials;
    const ibansAvailability = haveAndCanAddCredentials(customer, "ibans", "ibans");
    const haveIbans = ibansAvailability.haveCredentials;
    const canAddIbans = ibansAvailability.canAddCredentials;
    const userCreditCardsAvailability = haveAndCanAddCredentials(
      customer,
      "userCreditCards",
      "userCreditCards"
    );
    const haveUserCreditCards = userCreditCardsAvailability.haveCredentials;
    const canAddUserCreditCards = userCreditCardsAvailability.canAddCredentials;
    const identifiersAvailability = haveAndCanAddCredentials(
      customer,
      "identifiers",
      "identifiers"
    );
    const haveIdentifiers = identifiersAvailability.haveCredentials;
    const canAddIdentifiers = identifiersAvailability.canAddCredentials;
    if (haveCombos && canAddCombos) {
      options.push("combolists");
    }
    if (haveBirthDates && canAddBirthDates) {
      options.push("birthDates");
    }
    if (havePhones && canAddPhones) {
      options.push("phones");
    }
    if (haveAddresses && canAddAddresses) {
      options.push("addresses");
    }
    if (haveIbans && canAddIbans) {
      options.push("ibans");
    }
    if (haveUserCreditCards && canAddUserCreditCards) {
      options.push("userCreditCards");
    }
    if (haveIdentifiers && canAddIdentifiers) {
      options.push("identifiers");
    }
    return options;
  }

  function OtherCredentialsBox({ client }) {
    const [otherCredentialsDetailsOpen, setOtherCredentialsDetailsOpen] = useState(false);
    const [otherCredentialsNumber, setOtherCredentialsNumber] = useState(0);
    const [displayNumber, setDisplayNumber] = useState(0);
    const [isAnimating, setIsAnimating] = useState(false);
    const prevValue = useRef(0);
    const animationRef = useRef(null);
    const hasAnimatedRef = useRef(false);

    useEffect(() => {
      let totalNumber = 0;

      if (client) {
        if (client.creditcards && client.creditcards.length > 0) {
          totalNumber += client.creditcards.length;
        }
        if (client.combolists && client.combolists.length > 0) {
          totalNumber += client.combolists.length;
        }
        if (client.cookies && client.cookies.length > 0) {
          totalNumber += client.cookies.length;
        }
        if (client.cookiesClient && client.cookiesClient.length > 0) {
          totalNumber += client.cookiesClient.length;
        }
        if (client.passwords && client.passwords.length > 0) {
          totalNumber += client.passwords.length;
        }
        if (client.passwordsUrl && client.passwordsUrl.length > 0) {
          totalNumber += client.passwordsUrl.length;
        }
        if (client.passwordsApps && client.passwordsApps.length > 0) {
          totalNumber += client.passwordsApps.length;
        }
        if (client.usernames && client.usernames.length > 0) {
          totalNumber += client.usernames.length;
        }
        if (client.ransomwares && client.ransomwares.length > 0) {
          totalNumber += client.ransomwares.length;
        }
        if (client.phishingCanaryWhitelist && client.phishingCanaryWhitelist.length > 0) {
          totalNumber += client.phishingCanaryWhitelist.length;
        }
      }

      setOtherCredentialsNumber(totalNumber);
    }, [client]);

    useEffect(() => {
      if (otherCredentialsNumber === 0) {
        setDisplayNumber(0);
        hasAnimatedRef.current = false;
        return;
      }

      const shouldAnimate = !hasAnimatedRef.current || prevValue.current !== otherCredentialsNumber;

      if (!shouldAnimate) {
        setDisplayNumber(otherCredentialsNumber);
        return;
      }

      setIsAnimating(true);
      hasAnimatedRef.current = true;

      const startTime = Date.now();
      const startValue = prevValue.current;
      const endValue = otherCredentialsNumber;
      const duration = 2000;

      const animate = () => {
        const now = Date.now();
        const elapsed = now - startTime;
        const progress = Math.min(elapsed / duration, 1);

        const easeOutQuart = 1 - Math.pow(1 - progress, 4);
        const currentValue = Math.floor(startValue + (endValue - startValue) * easeOutQuart);

        setDisplayNumber(currentValue);

        if (progress < 1) {
          animationRef.current = requestAnimationFrame(animate);
        } else {
          setIsAnimating(false);
          prevValue.current = endValue;
          setTimeout(() => {
            setIsAnimating(false);
          }, 100);
        }
      };

      animationRef.current = requestAnimationFrame(animate);

      return () => {
        if (animationRef.current) {
          cancelAnimationFrame(animationRef.current);
        }
      };
    }, [otherCredentialsNumber]);

    const handleCloseOtherCredentialsDetails = () => {
      setOtherCredentialsDetailsOpen(false);
      setTimeout(() => {
        document.body.classList.remove("sweet-alert-open");
        document.body.style.overflow = "";
        document.body.style.paddingRight = "";
      }, 100);
    };

    const handleOpenOtherCredentialsDetails = () => {
      document.body.classList.add("sweet-alert-open");
      document.body.style.overflow = "hidden";
      setOtherCredentialsDetailsOpen(true);
    };

    const renderModal = () => {
      if (!otherCredentialsDetailsOpen) return null;

      return createPortal(
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "rgba(0, 0, 0, 0.6)",
          }}
        >
          <SweetAlert
            style={{
              display: "block",
              position: "relative",
              textAlign: "left",
              margin: 0,
              maxWidth: "90vw",
              maxHeight: "90vh",
              overflow: "hidden",
            }}
            title={t("user.other_credentials")}
            onConfirm={handleCloseOtherCredentialsDetails}
            onCancel={handleCloseOtherCredentialsDetails}
            closeOnClickOutside
            confirmBtnText={t("ok")}
            confirmBtnCssClass="popupCloseButton"
            confirmBtnStyle={{ boxShadow: "none" }}
          >
            <OtherCredentials client={client} />
          </SweetAlert>
        </div>,
        document.body
      );
    };

    return (
      <>
        {renderModal()}

        <div
          className={`user_box_container enhanced-credential-box other-credentials-box ${otherCredentialsNumber === 0 ? "empty-state" : ""}`}
        >
          <div className="box-decoration-element"></div>
          <div className="box-icon-container">
            <SecurityIcon className="box-main-icon" />
          </div>
          <div className="user_box_title">{t("user.other_credentials")}</div>
          <div className="user_box_subtitle">{t("user.currently_other_credentials_monitored")}</div>

          {otherCredentialsNumber === 0 ? (
            <div className="empty-state-message">
              <div className="empty-state-icon">
                <SecurityIcon />
              </div>
              <div className="empty-state-text">{t("user.no_credentials_title")}</div>
              <div className="empty-state-subtext">{t("user.no_credentials_text")}</div>
            </div>
          ) : (
            <>
              <div
                className={`user_box_number_credentials_monitored ${isAnimating ? "animating" : ""}`}
              >
                {displayNumber}
                <div className="number-pulse-effect"></div>
              </div>
              <div className="box-action-container">
                <IconButton
                  onClick={handleOpenOtherCredentialsDetails}
                  className="enhanced-details-button"
                  size="small"
                  disableRipple
                >
                  <VisibilityIcon className="button-icon" />
                  <span>{t("user.show_credentials_details")}</span>
                </IconButton>
              </div>
            </>
          )}
        </div>
      </>
    );
  }

  OtherCredentialsBox.propTypes = {
    client: PropTypes.object.isRequired,
  };

  function getCustomerData(customer) {
    return (
      <div style={{ fontSize: "16px" }}>
        <div className="user_boxes_wrapper">
          <GeneralInformation client={customer} />
          <ContractOverview client={customer} />
          <PersonalCredentialsBox client={customer} />
          <OtherCredentialsBox client={customer} />
        </div>
        <div style={{ marginTop: "10px" }}>
          {customer?.service?.length > 0 && customer.service.includes("combo") ? (
            <div style={{ marginTop: "50px" }}>
              <div style={{ fontSize: "14px" }}>{t("user.add_credential_explanation")}</div>
              <IconButton
                onClick={() => {
                  setAddingCredential(true);
                  setClientForCredential(customer.id);
                }}
                className="enhanced-action-button"
                size="small"
                disableRipple
              >
                {t("user.add_credential")}
              </IconButton>
            </div>
          ) : null}
        </div>
      </div>
    );
  }

  const renderGlobalModals = () => {
    const modals = [];

    if (addingCredentialsResult) {
      modals.push(
        createPortal(
          <div
            key="adding-credentials-result"
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 9999,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "rgba(0, 0, 0, 0.6)",
            }}
          >
            <SweetAlert
              style={{
                display: "block",
                position: "relative",
                textAlign: "left",
                margin: 0,
                maxWidth: "90vw",
                maxHeight: "90vh",
              }}
              title={
                addingCredentialsResult === "success"
                  ? t("user.add_credential_success_title")
                  : t("user.add_credential_error_title")
              }
              success={addingCredentialsResult === "success"}
              onConfirm={() => setAddingCredentialsResult("")}
              onCancel={() => setAddingCredentialsResult("")}
              closeOnClickOutside
              confirmBtnText={t("ok")}
              confirmBtnCssClass="popupCloseButton"
              confirmBtnStyle={{ boxShadow: "none" }}
            >
              {addingCredentialsResult === "success"
                ? t("user.add_credential_success_text")
                : t("user.add_credential_error_text")}
            </SweetAlert>
          </div>,
          document.body
        )
      );
    }

    if (changePasswordSuccess) {
      modals.push(
        createPortal(
          <div
            key="change-password-success"
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 9999,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "rgba(0, 0, 0, 0.6)",
            }}
          >
            <SweetAlert
              style={{
                display: "block",
                position: "relative",
                textAlign: "left",
                margin: 0,
                maxWidth: "90vw",
                maxHeight: "90vh",
              }}
              title={t("user.change_password")}
              success
              onConfirm={() => setChangePasswordSuccess(false)}
              onCancel={() => setChangePasswordSuccess(false)}
              closeOnClickOutside
              confirmBtnText={t("ok")}
              confirmBtnCssClass="popupCloseButton"
              confirmBtnStyle={{ boxShadow: "none" }}
            >
              {t("user.change_password_success_text")}
            </SweetAlert>
          </div>,
          document.body
        )
      );
    }

    if (changePasswordError) {
      modals.push(
        createPortal(
          <div
            key="change-password-error"
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 9999,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "rgba(0, 0, 0, 0.6)",
            }}
          >
            <SweetAlert
              style={{
                display: "block",
                position: "relative",
                textAlign: "left",
                margin: 0,
                maxWidth: "90vw",
                maxHeight: "90vh",
              }}
              title={t("user.change_password")}
              success={false}
              error
              onConfirm={() => setChangePasswordError(false)}
              onCancel={() => setChangePasswordError(false)}
              closeOnClickOutside
              confirmBtnText={t("ok")}
              confirmBtnCssClass="popupCloseButton"
              confirmBtnStyle={{ boxShadow: "none" }}
            >
              {t("user.change_password_error_text")}
            </SweetAlert>
          </div>,
          document.body
        )
      );
    }

    return modals;
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <h1
        className="card-title"
        style={{ fontSize: "28px", marginBottom: "10px", padding: "10px" }}
      >
        {user && user.name ? `${t("user.hello")}, ${user.name}` : t("user.hello")}
      </h1>

      {renderGlobalModals()}

      {addingCredential ? (
        <AddCredential
          closeFunction={() => {
            setAddingCredential(false);
          }}
          availableOptions={getAvailabilityOfCredentialsOptions(clientForCredential)}
          clientForCredential={clientForCredential}
          successFunction={() => {
            setAddingCredential(false);
            setAddingCredentialsResult("success");
          }}
          errorFunction={() => {
            setAddingCredential(false);
            setAddingCredentialsResult("error");
          }}
        />
      ) : null}

      {changingPassword ? (
        <ChangePassword
          closeFunction={() => {
            setChangingPassword(false);
          }}
          successFunction={() => {
            setChangingPassword(false);
            setChangePasswordSuccess(true);
          }}
          errorFunction={() => {
            setChangingPassword(false);
            setChangePasswordError(true);
          }}
        />
      ) : null}

      {user?.id ? (
        <div className="enhanced-user-info-section">
          <div className="user-info-grid">
            <div className="enhanced-info-card user-details-card">
              <div className="card-header">
                <div className="card-icon-wrapper">
                  <PersonIcon className="card-icon" />
                </div>
                <h3 className="card-title">Account Details</h3>
              </div>
              <div className="card-content">
                {user?.email ? (
                  <div className="info-row">
                    <EmailIcon className="info-icon" />
                    <div className="info-content">
                      <span className="info-label">{t("user.email")}</span>
                      <span className="info-value">{user.email}</span>
                    </div>
                  </div>
                ) : null}

                <div className="info-row">
                  <LocationOnIcon className="info-icon" />
                  <div className="info-content">
                    <span className="info-label">{t("user.country")}</span>
                    {user?.country && !editUserCountry ? (
                      <div className="info-value-with-action">
                        <span className="info-value">{t(`countryCode.${user.country}`)}</span>
                        <button
                          onClick={() => setEditUserCountry(true)}
                          className="inline-action-btn"
                        >
                          Change
                        </button>
                      </div>
                    ) : (
                      <div className="country-edit-section">
                        {!user?.country && !editUserCountry ? (
                          <button
                            onClick={() => setEditUserCountry(true)}
                            className="inline-action-btn"
                          >
                            {t("user.add_country")}
                          </button>
                        ) : null}
                        {editUserCountry ? (
                          <div className="country-select-wrapper">
                            <Autocomplete
                              label={t("user.new_country")}
                              onSelect={changeUserCountry}
                              list={Object.keys(countryCode).map((el) => ({
                                label: t(`countryCode.${el}`),
                                value: el,
                              }))}
                            />
                            <button
                              onClick={() => setEditUserCountry(false)}
                              className="cancel-btn"
                            >
                              {t("cancel")}
                            </button>
                          </div>
                        ) : null}
                      </div>
                    )}
                  </div>
                </div>

                <div className="card-action-section">
                  <button
                    onClick={() => setChangingPassword(true)}
                    className="enhanced-action-button with-icon"
                  >
                    <LockIcon className="button-icon" />
                    {t("user.change_password")}
                  </button>
                </div>
              </div>
            </div>

            <div className="enhanced-info-card customers-card">
              <div className="card-header">
                <div className="card-icon-wrapper">
                  <GroupIcon className="card-icon" />
                </div>
                <h3 className="card-title">Your Customers</h3>
              </div>
              <div className="card-content">
                {customers && customers.length > 0 ? (
                  <>
                    <div className="customers-summary">
                      <span className="summary-number">{customers.length}</span>
                      <span className="summary-label">{t("user.customers")}</span>
                    </div>
                    <div className="customers-list">
                      {customers.map((customer) => (
                        <div
                          key={`customer_short_list_${customer.id.toString()}`}
                          className="customer-item"
                        >
                          <div className="customer-avatar">
                            {customer.name ? customer.name.charAt(0).toUpperCase() : "C"}
                          </div>
                          <span className="customer-name">{customer.name || "Customer"}</span>
                        </div>
                      ))}
                    </div>
                  </>
                ) : (
                  <div className="empty-state">
                    <GroupIcon className="empty-icon" />
                    <p>{t("user.no_clients")}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      ) : (
        <ServerError />
      )}

      {customers && customers.length > 0
        ? customers.map((customer, _index) => (
            <div key={`customer-big-${customer.id}`} style={{ marginTop: "30px" }}>
              <MDBox
                className="boxes-border-radius enhanced-customer-section"
                padding={2.3}
                sx={{ background: "white" }}
              >
                <h4 className="customer-section-title">{customer.name || t("user.customer")}</h4>
                {customer ? getCustomerData(customer) : null}
              </MDBox>
            </div>
          ))
        : null}

      <div className="enhanced-notifications-section">
        <div className="notifications-grid">
          <form className="notifications-form" onSubmit={handleFormSubmit}>
            <div className="form-card">
              <div className="form-card-header">
                <AssessmentIcon className="form-icon" />
                <h4>{t("user.periodic_reports")}</h4>
              </div>
              <div className="form-card-content">
                <label className="enhanced-checkbox">
                  <input
                    id="reports.weekly"
                    type="checkbox"
                    name="reports.weekly"
                    checked={form.reports.weekly}
                    onChange={handleChange}
                  />
                  <span className="checkbox-custom"></span>
                  <span className="checkbox-label">{t("user.weekly")}</span>
                </label>
                <label className="enhanced-checkbox">
                  <input
                    id="reports.monthly"
                    type="checkbox"
                    name="reports.monthly"
                    checked={form.reports.monthly}
                    onChange={handleChange}
                  />
                  <span className="checkbox-custom"></span>
                  <span className="checkbox-label">{t("user.monthly")}</span>
                </label>
                <label className="enhanced-checkbox">
                  <input
                    id="reports.quarterly"
                    type="checkbox"
                    name="reports.quarterly"
                    checked={form.reports.quarterly}
                    onChange={handleChange}
                  />
                  <span className="checkbox-custom"></span>
                  <span className="checkbox-label">{t("user.quarterly")}</span>
                </label>
              </div>
            </div>

            <div className="form-card">
              <div className="form-card-header">
                <NotificationsIcon className="form-icon" />
                <h4>{t("user.alerts")}</h4>
              </div>
              <div className="form-card-content">
                <label className="enhanced-checkbox">
                  <input
                    type="checkbox"
                    id="alerts.email"
                    name="alerts.email"
                    checked={form.alerts.email}
                    onChange={handleChange}
                  />
                  <span className="checkbox-custom"></span>
                  <span className="checkbox-label">{t("user.email")}</span>
                </label>
                <label className="enhanced-checkbox">
                  <input
                    type="checkbox"
                    id="alerts.push"
                    name="alerts.push"
                    checked={form.alerts.push}
                    onChange={handleChange}
                  />
                  <span className="checkbox-custom"></span>
                  <span className="checkbox-label">{t("user.push")}</span>
                </label>
              </div>
            </div>

            <div className="form-card">
              <div className="form-card-header">
                <CampaignIcon className="form-icon" />
                <h4>Marketing</h4>
              </div>
              <div className="form-card-content">
                <label className="enhanced-checkbox">
                  <input
                    type="checkbox"
                    id="marketing.email"
                    name="marketing.email"
                    checked={form.marketing.email}
                    onChange={handleChange}
                  />
                  <span className="checkbox-custom"></span>
                  <span className="checkbox-label">{t("user.email")}</span>
                </label>
                <label className="enhanced-checkbox">
                  <input
                    type="checkbox"
                    id="marketing.push"
                    name="marketing.push"
                    checked={form.marketing.push}
                    onChange={handleChange}
                  />
                  <span className="checkbox-custom"></span>
                  <span className="checkbox-label">{t("user.push")}</span>
                </label>
              </div>
            </div>

            <div className="form-submit-section">
              <button
                className="enhanced-submit-button"
                type="submit"
                disabled={submitState.loading}
              >
                {submitState.loading ? (
                  <span className="loading-spinner"></span>
                ) : (
                  <>
                    <CheckCircleIcon className="button-icon" />
                    {t("user.notification_form_btn")}
                  </>
                )}
              </button>

              {submitState.success && (
                <div className="enhanced-success-message">
                  <CheckCircleIcon className="success-icon" />
                  {t("user.success")}
                </div>
              )}

              {submitState.error && (
                <div className="enhanced-error-message">{submitState.error}</div>
              )}
            </div>
          </form>
        </div>
      </div>
    </DashboardLayout>
  );
}

export default UserPage;
